import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, take, map } from 'rxjs/operators';
import { AvatarActions } from './avatar.actions';
import {
  selectAvatars,
  selectActiveAvatar,
  selectAvatarLoading,
  selectAvatarCreating,
  selectDeletingAvatarId,
  selectDeletingAllAvatars,
  selectAvatarError,
  selectHasAvatars,
  selectHasActiveAvatar,
  selectAvatarById,
  selectIsAvatarDeleting,
  selectIsCacheValid,
  selectIsAvatarsLoaded,
  selectActiveAvatarId,
  selectSettingActiveAvatar
} from './avatar.selectors';
import { Avatar, AvatarUpdateRequest } from '@app/core/api/avatar';
import { Gender } from '@app/shared/models';

/**
 * Facade service for avatar state management
 * Provides clean API for components to interact with avatar NgRx store
 */
@Injectable({
  providedIn: 'root'
})
export class AvatarFacade {
  private readonly store = inject(Store);

  // Observables
  readonly avatars$ = this.store.select(selectAvatars);
  readonly activeAvatar$ = this.store.select(selectActiveAvatar);
  readonly loading$ = this.store.select(selectAvatarLoading);
  readonly creating$ = this.store.select(selectAvatarCreating);
  readonly deletingAvatarId$ = this.store.select(selectDeletingAvatarId);
  readonly deletingAllAvatars$ = this.store.select(selectDeletingAllAvatars);
  readonly settingActiveAvatar$ = this.store.select(selectSettingActiveAvatar);
  readonly error$ = this.store.select(selectAvatarError);
  readonly hasAvatars$ = this.store.select(selectHasAvatars);
  readonly hasActiveAvatar$ = this.store.select(selectHasActiveAvatar);
  readonly isCacheValid$ = this.store.select(selectIsCacheValid);
  readonly isAvatarsLoaded$ = this.store.select(selectIsAvatarsLoaded);
  readonly activeAvatarId$ = this.store.select(selectActiveAvatarId);

  // Actions
  loadAvatars(page?: number, size?: number): void {
    this.store.dispatch(AvatarActions.loadAvatars({ page, size }));
  }

  createAvatar(image: File, faceName?: string, gender?: Gender): void {
    this.store.dispatch(AvatarActions.createAvatar({ image, faceName, gender }));
  }

  getAvatarStatus(characterUid: string): void {
    this.store.dispatch(AvatarActions.getAvatarStatus({ characterUid }));
  }

  updateAvatar(id: string, updates: AvatarUpdateRequest): void {
    this.store.dispatch(AvatarActions.updateAvatar({ id, updates }));
  }

  deleteAvatar(id: string): void {
    this.store.dispatch(AvatarActions.deleteAvatar({ id }));
  }

  deleteAllAvatars(): void {
    this.store.dispatch(AvatarActions.deleteAllAvatars());
  }

  setActiveAvatar(avatarId: string): void {
    this.store.dispatch(AvatarActions.setActiveAvatar({ avatarId }));
  }

  removeActiveAvatar(): void {
    this.store.dispatch(AvatarActions.removeActiveAvatar());
  }

  initializeAvatars(): void {
    this.store.dispatch(AvatarActions.initializeAvatars());
  }

  clearAvatars(): void {
    this.store.dispatch(AvatarActions.clearAvatars());
  }

  invalidateCache(): void {
    this.store.dispatch(AvatarActions.invalidateCache());
  }

  // Selector helpers
  getAvatarById(avatarId: string): Observable<Avatar | null> {
    return this.store.select(selectAvatarById(avatarId));
  }

  isAvatarDeleting(avatarId: string): Observable<boolean> {
    return this.store.select(selectIsAvatarDeleting(avatarId));
  }

  // Helper method to wait for avatars to load (similar to UserFacade.waitForProfile)
  waitForAvatars(): Observable<Avatar[]> {
    return this.avatars$.pipe(
      filter(avatars => avatars.length > 0),
      take(1)
    );
  }

  // Get current active avatar synchronously
  getCurrentActiveAvatar(): Observable<Avatar | null> {
    return this.activeAvatar$.pipe(take(1));
  }

  // Helper to check if an avatar is the active one
  isActiveAvatar(avatarId: string): Observable<boolean> {
    return this.activeAvatarId$.pipe(
      map(activeId => activeId === avatarId)
    );
  }
}
